import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { useGetOrganizationMemberships } from 'api/queries/organizations/memberships';
import Redirect from 'legacy/components/util/Redirect/Redirect';
import { FLAGS } from 'legacy/common/constants/launchdarkly';
import ProductsList from './components/steps/ProductsList';
import { CheckoutSteps } from './types';
import CheckoutSkeleton from 'components/checkout/CheckoutSkeleton';
import CheckoutProvider from './context';
import { useGetGroup } from 'legacy/groups/hooks';
import Summary from './components/steps/Summary';
import Success from './components/steps/Success';
import { useGetCurrentUserOrganizationsMemberships } from 'api/queries/users/memberships';

const STEPS_MAP = {
  [CheckoutSteps.ProductsList]: ProductsList,
  [CheckoutSteps.Summary]: Summary,
  [CheckoutSteps.Success]: Success
};

function OrganizationMembershipsCheckout() {
  const [step, setStep] = useState<CheckoutSteps>(CheckoutSteps.ProductsList);
  const router = useRouter();
  const flags = useFlags();
  const { query } = router;
  const organizationId = query.organizationId as string;
  const { data: orgMemberships, isLoading: isOrgMembershipsLoading } =
    useGetOrganizationMemberships({
      organizationId,
      enabled: Boolean(organizationId && flags[FLAGS.ENABLE_ORG_MEMBERSHIPS])
    });

  const { data: userMemberships, isLoading: isUserMembershipsLoading } =
    useGetCurrentUserOrganizationsMemberships({
      enabled: Boolean(organizationId && flags[FLAGS.ENABLE_ORG_MEMBERSHIPS])
    });

  const { data: organization, isLoading: isOrganizationLoading } = useGetGroup({
    groupId: organizationId
  });

  useEffect(() => {
    window.scroll({
      top: 0,
      behavior: 'smooth'
    });
  }, [step]);

  if (!flags[FLAGS.ENABLE_ORG_MEMBERSHIPS]) {
    return <Redirect to={`/organizations/${organizationId}`} />;
  }

  const StepComponent = STEPS_MAP[step];
  const goToStep = (step: CheckoutSteps) => {
    setStep(step);
  };

  if (
    isOrgMembershipsLoading ||
    isOrganizationLoading ||
    isUserMembershipsLoading
  ) {
    return <CheckoutSkeleton />;
  }

  if (orgMemberships?.length === 0) {
    return <Redirect to={`/organizations/${organizationId}`} />;
  }

  return (
    <CheckoutProvider
      products={orgMemberships}
      goToStep={goToStep}
      currentStep={step}
      organization={organization}
      userMemberships={userMemberships}
    >
      <StepComponent />
    </CheckoutProvider>
  );
}

export default OrganizationMembershipsCheckout;
